import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  StatTile,
  Tabs,
  type Column,
  useToast,
} from '@/components/ui';
import {
  type StockRow,
  useStockActions,
  useStockOverview,
} from '@/features/admin-v2/api';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { formatRM, parseRM } from '@/features/billing/money';
import { useDirectory } from '@/features/messaging/api';
import { useRegisterKinds } from '@/features/registers/api';
import { PageContainer } from '@/layouts/PageContainer';
import { api, isApiError } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { RegisterCard } from '@/pages/ops/OperationalRecords';
import { dayLabel } from '@/pages/ops/register-format';
import dash from '@/pages/dashboards/dashboards.module.css';
import styles from '@/pages/queue/queue.module.css';
import local from './stock.module.css';
import {
  CountDialog,
  DisposeDialog,
  PriceDialog,
  RejectOrderDialog,
  ReorderSettingsDialog,
  TransferDialog,
} from './StockDialogs';
import { downloadFile } from '@/features/admin-v2/download';
import { useStockFail } from './stock-fail';

type Tab =
  | 'stock'
  | 'expiry'
  | 'orders'
  | 'counts'
  | 'transfers'
  | 'fridge'
  | 'recalls'
  | 'prices'
  | 'forecast';

type StockWorkspaceTab = Tab | 'controlled-drugs';

const TABS: { value: Tab; label: string }[] = [
  { value: 'stock', label: 'Stock' },
  { value: 'expiry', label: 'Expiry' },
  { value: 'orders', label: 'Orders' },
  { value: 'counts', label: 'Counts' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'fridge', label: 'Fridge' },
  { value: 'recalls', label: 'Recalls' },
  { value: 'prices', label: 'Prices' },
  { value: 'forecast', label: 'Forecast' },
];

interface ExpiryRow {
  id: string;
  medicine: string;
  psychotropic: boolean;
  batchNo: string;
  expiry: string;
  qty: number;
  status: string;
  daysLeft: number;
}
interface OrderRow {
  id: string;
  number: string;
  status: string;
  supplierId: string;
  createdById: string;
  supplier: { name: string } | null;
  lines: {
    id: string;
    qty: number;
    receivedQty?: number;
    medicine: { id: string; name: string };
  }[];
  createdAt: string;
  approvedAt: string | null;
  receivedAt: string | null;
}
interface CountRow {
  id: string;
  kind: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  lines: number;
  counted: number;
  differences: number;
}
interface TransferRow {
  id: string;
  number: string;
  status: string;
  sentAt: string;
  receivedAt: string | null;
  incoming: boolean;
  fromBranch: { name: string };
  toBranch: { name: string };
}
interface PriceRow {
  id: string;
  name: string;
  unit: string;
  unitPriceSen: number | null;
  packSize: number;
  packPriceSen: number | null;
}
interface Forecast {
  method: string;
  aiConnected: boolean;
  rows: {
    medicineId: string;
    medicine: string;
    perDay: number;
    leadTimeDays: number;
    current: number;
    suggested: number;
    parLevel: number;
  }[];
}
interface Supplier {
  id: string;
  name: string;
  orderDay: number | null;
  leadTimeDays: number;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function useStockList<T>(path: string, enabled = true) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['stock', path, selected?.id],
    queryFn: ({ signal }) => api.get<T>(path, { signal }).then((r) => r.data),
    enabled: enabled && !!selected,
  });
}

function stockState(r: StockRow): {
  label: string;
  tone: 'success' | 'warning' | 'danger';
} {
  if (r.status === 'out') return { label: 'Out of stock', tone: 'danger' };
  if (r.status === 'low') return { label: 'Low stock', tone: 'warning' };
  return { label: 'In stock', tone: 'success' };
}

/** "Stock & expiry" (prototype stock(): tiles, one card with eleven-ish tabs). */
export function StockPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const [tab, setTab] = useUrlState<Tab>(
    'tab',
    'stock',
    TABS.map(({ value }) => value),
  );
  const [drawer, setDrawer] = useUrlState<'closed' | 'receive'>(
    'drawer',
    'closed',
    ['closed', 'receive'],
  );
  const receiving = drawer === 'receive';
  const stock = useStockOverview();
  const expiry = useStockList<ExpiryRow[]>('/stock/expiry');
  const suppliers = useStockList<Supplier[]>('/stock/suppliers');
  const rows = stock.data ?? [];
  const low = rows.filter((r) => r.status !== 'ok');
  const exp = expiry.data ?? [];
  const within30 = exp.filter((b) => b.daysLeft >= 0 && b.daysLeft <= 30);
  const blocked = exp.filter((b) => b.daysLeft < 0 || b.status !== 'available');
  const today = new Date().getDay();
  const nextDay = (suppliers.data ?? [])
    .filter((s) => s.orderDay !== null)
    .map((s) => ({ s, inDays: ((s.orderDay as number) - today + 7) % 7 }))
    .sort((a, b) => a.inDays - b.inDays)[0];
  const tabs: { value: StockWorkspaceTab; label: string }[] = [
    ...TABS,
    ...(hasPermission('dispensary:read')
      ? [
          {
            value: 'controlled-drugs' as const,
            label: t('dispensary.controlledDrugsTab'),
          },
        ]
      : []),
  ];

  if (!branch)
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose a branch to see its shelf stock and cycle counts.
        </p>
      </PageContainer>
    );

  return (
    <PageContainer>
      <div className={dash.tiles}>
        <StatTile
          highlight
          label="Low or out of stock"
          value={stock.data ? low.length : '—'}
          caption={
            low
              .slice(0, 3)
              .map((r) => r.name)
              .join(', ') || undefined
          }
        />
        <StatTile
          label="Expiring within 30 days"
          value={expiry.data ? within30.length : '—'}
          caption="batches"
        />
        <StatTile
          label="Expired, quarantined"
          value={expiry.data ? blocked.length : '—'}
          caption="blocked from dispensing"
        />
        <StatTile
          label="Next stock-up day"
          value={nextDay ? DAYS[nextDay.s.orderDay as number] : '—'}
          caption={nextDay?.s.name}
        />
      </div>
      <Card
        title={`Stock & expiry · ${branch.name}`}
        flush
        actions={
          hasPermission('stock:manage') && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => setDrawer('receive')}
            >
              Receive stock
            </Button>
          )
        }
      >
        <Tabs
          label="Stock"
          items={tabs}
          value={tab}
          onChange={(next) =>
            next === 'controlled-drugs'
              ? void navigate('/controlled-drugs')
              : setTab(next)
          }
        />
        {tab === 'stock' && (
          <StockTab rows={rows} loading={stock.isLoading} error={stock.error} />
        )}
        {tab === 'expiry' && (
          <ExpiryTab rows={expiry.data} loading={expiry.isLoading} />
        )}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'counts' && <CountsTab />}
        {tab === 'transfers' && <TransfersTab />}
        {tab === 'prices' && <PricesTab />}
        {tab === 'forecast' && <ForecastTab />}
      </Card>
      {(tab === 'fridge' || tab === 'recalls') && (
        <RegisterTab kind={tab === 'fridge' ? 'fridge_reading' : 'recall'} />
      )}
      {receiving && (
        <ReceiveDrawer
          medicines={rows}
          suppliers={suppliers.data ?? []}
          onClose={() => setDrawer('closed')}
        />
      )}
    </PageContainer>
  );
}

function StockTab({
  rows,
  loading,
  error,
}: {
  rows: StockRow[];
  loading: boolean;
  error: unknown;
}) {
  // RL-1653: reorder point, par level and supplier per medicine.
  const { hasPermission } = useSession();
  const suppliers = useStockList<Supplier[]>('/stock/suppliers');
  const [editing, setEditing] = useState<StockRow | null>(null);
  const columns: Column<StockRow>[] = [
    {
      key: 'medicine',
      header: 'Medicine',
      render: (r) => (
        <>
          <p className={styles.name}>{r.name}</p>
          <p className={styles.sub}>
            {[
              r.unit,
              r.flags.psychotropic
                ? 'psychotropic'
                : r.flags.controlled
                  ? 'controlled'
                  : '',
              r.flags.fridge ? 'fridge' : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </>
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (r) => r.supplier?.name ?? '-',
    },
    {
      key: 'usable',
      header: 'Usable',
      align: 'right',
      render: (r) => <strong>{`${r.usable} ${r.unit}`}</strong>,
    },
    {
      key: 'days',
      header: 'Days left',
      align: 'right',
      render: (r) => (r.daysLeft === null ? '-' : r.daysLeft),
    },
    {
      key: 'reorder',
      header: 'Reorder at',
      align: 'right',
      render: (r) => r.reorderPoint,
    },
    {
      key: 'expiry',
      header: 'Next expiry',
      render: (r) => (r.nextExpiry ? dayLabel(r.nextExpiry) : '-'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const s = stockState(r);
        return (
          <Badge tone={s.tone} dot={s.tone !== 'success'}>
            {s.label}
          </Badge>
        );
      },
    },
    ...(hasPermission('stock:manage')
      ? [
          {
            key: 'act',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right' as const,
            render: (r: StockRow) => (
              <Button size="sm" onClick={() => setEditing(r)}>
                Reorder settings
              </Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <DataTable
        caption="Stock"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        error={error}
        empty={
          <EmptyState
            title="No medicines"
            body="Medicines appear once the stock list is set up."
          />
        }
      />
      {editing && (
        <ReorderSettingsDialog
          medicine={editing}
          suppliers={suppliers.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function ExpiryTab({
  rows,
  loading,
}: {
  rows: ExpiryRow[] | undefined;
  loading: boolean;
}) {
  // RL-1651: write off or return a batch, with a witness (someone else).
  const { hasPermission, me } = useSession();
  const dir = useDirectory(hasPermission('stock:manage'));
  const [removing, setRemoving] = useState<ExpiryRow | null>(null);
  return (
    <>
      <DataTable
        caption="Expiry"
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        empty={<EmptyState title="Nothing expiring within 180 days" />}
        columns={[
          {
            key: 'medicine',
            header: 'Medicine',
            render: (r) => <strong>{r.medicine}</strong>,
          },
          {
            key: 'batch',
            header: 'Batch',
            render: (r) => <span className="mono">{r.batchNo}</span>,
          },
          {
            key: 'qty',
            header: 'Quantity',
            align: 'right',
            render: (r) => r.qty,
          },
          {
            key: 'expiry',
            header: 'Expiry',
            render: (r) => dayLabel(r.expiry),
          },
          {
            key: 'left',
            header: 'Status',
            render: (r) => (
              <Badge
                tone={
                  r.daysLeft < 0
                    ? 'danger'
                    : r.daysLeft <= 30
                      ? 'danger'
                      : r.daysLeft <= 90
                        ? 'warning'
                        : 'neutral'
                }
              >
                {r.daysLeft < 0
                  ? 'Expired'
                  : `Within ${r.daysLeft <= 30 ? 30 : r.daysLeft <= 90 ? 90 : 180} days`}
              </Badge>
            ),
          },
          {
            key: 'a',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) =>
              hasPermission('stock:manage') ? (
                <Button size="sm" onClick={() => setRemoving(r)}>
                  {r.daysLeft < 0 ? 'Write off' : 'Remove'}
                </Button>
              ) : null,
          },
        ]}
      />
      {removing && (
        <DisposeDialog
          batch={removing}
          witnesses={(dir.data?.staff ?? []).filter((s) => s.id !== me?.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </>
  );
}

function OrdersTab() {
  const { hasPermission, me } = useSession();
  // The person who drafted an order cannot also approve or reject it.
  const canDecide = (r: OrderRow) =>
    r.status === 'draft' &&
    hasPermission('purchase_orders:approve') &&
    r.createdById !== me?.id;
  const q = useStockList<OrderRow[]>('/stock/orders');
  const qc = useQueryClient();
  const actions = useStockActions();
  const toast = useToast();
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/stock/orders/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  // RL-1650: receive an approved order.
  const stock = useStockOverview();
  const suppliers = useStockList<Supplier[]>('/stock/suppliers');
  const [receiving, setReceiving] = useState<OrderRow | null>(null);
  // RL-1653: reject a draft; download any order as a PDF for the supplier.
  const [rejecting, setRejecting] = useState<OrderRow | null>(null);
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  return (
    <>
      {hasPermission('stock:manage') && (
        <div className={local.cardBar}>
          <Button
            size="sm"
            loading={actions.draftOrder.isPending}
            onClick={() =>
              void actions.draftOrder
                .mutateAsync()
                .then(() =>
                  toast.show({ tone: 'success', title: 'Reorder drafted' }),
                )
                .catch(fail)
            }
          >
            Draft reorder up to par
          </Button>
        </div>
      )}
      <DataTable
        caption="Purchase orders"
        rows={q.data}
        loading={q.isLoading}
        error={q.error}
        rowKey={(r) => r.id}
        empty={<EmptyState title="No purchase orders" />}
        columns={[
          {
            key: 'n',
            header: 'Order',
            render: (r) => <span className="mono">{r.number}</span>,
          },
          {
            key: 's',
            header: 'Supplier',
            render: (r) => r.supplier?.name ?? '-',
          },
          {
            key: 'l',
            header: 'Items',
            render: (r) =>
              r.lines.map((l) => `${l.medicine.name} ×${l.qty}`).join(', '),
          },
          { key: 'd', header: 'Created', render: (r) => dayLabel(r.createdAt) },
          {
            key: 'st',
            header: 'Status',
            render: (r) => (
              <Badge
                tone={
                  r.status === 'received'
                    ? 'success'
                    : r.status === 'approved'
                      ? 'info'
                      : 'neutral'
                }
              >
                {r.status === 'cancelled'
                  ? 'Rejected'
                  : r.status.charAt(0).toUpperCase() + r.status.slice(1)}
              </Badge>
            ),
          },
          {
            key: 'a',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) => (
              <span className={local.rowActions}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void downloadFile(
                      `/stock/orders/${r.id}/pdf`,
                      `${r.number}.pdf`,
                    ).catch(fail)
                  }
                >
                  PDF
                </Button>
                {canDecide(r) && (
                  <Button size="sm" onClick={() => setRejecting(r)}>
                    Reject
                  </Button>
                )}
                {r.status === 'approved' && hasPermission('stock:manage') && (
                  <Button size="sm" onClick={() => setReceiving(r)}>
                    Receive
                  </Button>
                )}
                {canDecide(r) && (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={approve.isPending && approve.variables === r.id}
                    onClick={() =>
                      void approve
                        .mutateAsync(r.id)
                        .then(() =>
                          toast.show({
                            tone: 'success',
                            title: 'Order approved',
                          }),
                        )
                        .catch(fail)
                    }
                  >
                    Approve
                  </Button>
                )}
              </span>
            ),
          },
        ]}
      />
      {rejecting && (
        <RejectOrderDialog
          order={rejecting}
          onClose={() => setRejecting(null)}
        />
      )}
      {receiving && (
        <ReceiveDrawer
          medicines={stock.data ?? []}
          suppliers={suppliers.data ?? []}
          order={receiving}
          onClose={() => setReceiving(null)}
        />
      )}
    </>
  );
}

function CountsTab() {
  const { hasPermission } = useSession();
  const q = useStockList<CountRow[]>('/stock/counts');
  const actions = useStockActions();
  const toast = useToast();
  // RL-1649: open a count to enter it, compare and finish.
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <>
      {hasPermission('stock:manage') && (
        <div className={local.cardBar}>
          <Button
            size="sm"
            loading={actions.startCount.isPending}
            onClick={() =>
              void actions.startCount
                // RL-1649 (gap #27): the API takes "cycle" or "full".
                .mutateAsync('cycle')
                .then(() =>
                  toast.show({ tone: 'success', title: 'Cycle count started' }),
                )
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not done',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Start cycle count
          </Button>
        </div>
      )}
      <DataTable
        caption="Counts"
        rows={q.data}
        loading={q.isLoading}
        error={q.error}
        rowKey={(r) => r.id}
        empty={
          <EmptyState
            title="No counts yet"
            body="Counts hide the expected quantity until you compare."
          />
        }
        columns={[
          {
            key: 'k',
            header: 'Count',
            render: (r) => <strong>{r.kind}</strong>,
          },
          { key: 'd', header: 'Started', render: (r) => dayLabel(r.startedAt) },
          {
            key: 'p',
            header: 'Counted',
            align: 'right',
            render: (r) => `${r.counted}/${r.lines}`,
          },
          {
            key: 'x',
            header: 'Differences',
            align: 'right',
            render: (r) => r.differences,
          },
          {
            key: 's',
            header: 'Status',
            render: (r) => (
              <Badge tone={r.status === 'open' ? 'warning' : 'success'}>
                {r.status === 'open' ? 'Open' : 'Finished'}
              </Badge>
            ),
          },
          {
            key: 'a',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) => (
              <Button size="sm" onClick={() => setOpenId(r.id)}>
                {r.status === 'open' && hasPermission('stock:manage')
                  ? 'Count'
                  : 'View'}
              </Button>
            ),
          },
        ]}
      />
      {openId && (
        <CountDialog
          id={openId}
          canManage={hasPermission('stock:manage')}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function TransfersTab() {
  const q = useStockList<TransferRow[]>('/stock/transfers');
  // RL-1651: send stock to another branch; receive what comes in.
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const stock = useStockOverview();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const [sending, setSending] = useState(false);
  const receive = useMutation({
    mutationFn: (id: string) => api.post(`/stock/transfers/${id}/receive`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const canManage = hasPermission('stock:manage');
  return (
    <>
      {canManage && (
        <div className={local.cardBar}>
          <Button size="sm" onClick={() => setSending(true)}>
            Send transfer
          </Button>
        </div>
      )}
      <DataTable
        caption="Transfers"
        rows={q.data}
        loading={q.isLoading}
        error={q.error}
        rowKey={(r) => r.id}
        empty={
          <EmptyState
            title="No transfers"
            body="Stock sent between branches appears here."
          />
        }
        columns={[
          {
            key: 'n',
            header: 'Transfer',
            render: (r) => <span className="mono">{r.number}</span>,
          },
          {
            key: 'r',
            header: 'Route',
            render: (r) => `${r.fromBranch.name} → ${r.toBranch.name}`,
          },
          { key: 's', header: 'Sent', render: (r) => dayLabel(r.sentAt) },
          {
            key: 'st',
            header: 'Status',
            render: (r) => (
              <Badge tone={r.receivedAt ? 'success' : 'warning'}>
                {r.receivedAt ? 'Received' : r.incoming ? 'Incoming' : 'Sent'}
              </Badge>
            ),
          },
          {
            key: 'a',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) =>
              canManage && r.incoming && !r.receivedAt ? (
                <Button
                  size="sm"
                  variant="primary"
                  loading={receive.isPending && receive.variables === r.id}
                  onClick={() =>
                    void receive
                      .mutateAsync(r.id)
                      .then(() =>
                        toast.show({
                          tone: 'success',
                          title: 'Transfer received',
                        }),
                      )
                      .catch(fail)
                  }
                >
                  Receive
                </Button>
              ) : null,
          },
        ]}
      />
      {sending && (
        <TransferDialog
          // Controlled medicines are not transferred (prescription book rules).
          medicines={(stock.data ?? []).filter(
            (m) => !m.flags.controlled && !m.flags.psychotropic,
          )}
          fromBranchId={selected?.id ?? ''}
          onClose={() => setSending(false)}
        />
      )}
    </>
  );
}

function PricesTab() {
  const q = useStockList<PriceRow[]>('/stock/prices');
  // RL-1652: prices are edited here.
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState<PriceRow | null>(null);
  return (
    <>
      <DataTable
        caption="Prices"
        rows={q.data}
        loading={q.isLoading}
        error={q.error}
        rowKey={(r) => r.id}
        empty={<EmptyState title="No prices" />}
        columns={[
          {
            key: 'n',
            header: 'Medicine',
            render: (r) => <strong>{r.name}</strong>,
          },
          {
            key: 'u',
            header: 'Per unit',
            align: 'right',
            render: (r) =>
              r.unitPriceSen === null ? 'Not set' : formatRM(r.unitPriceSen),
          },
          {
            key: 'p',
            header: 'Pack',
            align: 'right',
            render: (r) =>
              r.packPriceSen === null
                ? 'Not set'
                : `${formatRM(r.packPriceSen)} / ${r.packSize}`,
          },
          {
            key: 'a',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) =>
              hasPermission('stock:manage') ? (
                <Button size="sm" onClick={() => setEditing(r)}>
                  Edit price
                </Button>
              ) : null,
          },
        ]}
      />
      {editing && (
        <PriceDialog medicine={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

function ForecastTab() {
  const q = useStockList<Forecast>('/stock/forecast');
  // RL-1652: apply the suggested reorder points (par at least twice).
  const { hasPermission } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useStockFail();
  const apply = useMutation({
    mutationFn: () => api.post('/stock/forecast/apply'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  const [confirming, setConfirming] = useState(false);
  const changing = (q.data?.rows ?? []).filter((r) => r.suggested > 0).length;
  return (
    <>
      {hasPermission('stock:manage') && changing > 0 && (
        <div className={local.cardBar}>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setConfirming(true)}
          >
            Apply suggested reorder points
          </Button>
        </div>
      )}
      {confirming && (
        <Dialog
          open
          onClose={() => setConfirming(false)}
          title="Apply suggested reorder points?"
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={apply.isPending}
                onClick={() =>
                  void apply
                    .mutateAsync()
                    .then(() => {
                      setConfirming(false);
                      toast.show({
                        tone: 'success',
                        title: 'Suggested reorder points applied',
                      });
                    })
                    .catch(fail)
                }
              >
                Apply
              </Button>
            </>
          }
        >
          <p>
            {`The reorder point and par level of ${changing} ${changing === 1 ? 'medicine' : 'medicines'} change to the suggestions. Medicines with no usage keep their settings.`}
          </p>
        </Dialog>
      )}
      <p className={local.cardNote}>
        Reorder points are suggested from usage and supplier lead time
        {q.data && !q.data.aiConnected
          ? ' (statistical; no AI model is connected)'
          : ''}
        .
      </p>
      <DataTable
        caption="Forecast"
        rows={q.data?.rows}
        loading={q.isLoading}
        error={q.error}
        rowKey={(r) => r.medicineId}
        empty={<EmptyState title="Nothing to forecast yet" />}
        columns={[
          {
            key: 'm',
            header: 'Medicine',
            render: (r) => <strong>{r.medicine}</strong>,
          },
          {
            key: 'u',
            header: 'Per day',
            align: 'right',
            render: (r) => r.perDay,
          },
          {
            key: 'l',
            header: 'Lead time',
            align: 'right',
            render: (r) => `${r.leadTimeDays} d`,
          },
          {
            key: 'c',
            header: 'Reorder at now',
            align: 'right',
            render: (r) => r.current,
          },
          {
            key: 's',
            header: 'Suggested',
            align: 'right',
            render: (r) => <strong>{r.suggested}</strong>,
          },
        ]}
      />
    </>
  );
}

function RegisterTab({ kind }: { kind: string }) {
  const kinds = useRegisterKinds();
  const k = kinds.data?.find((x) => x.kind === kind);
  if (!k) return null;
  return <RegisterCard kind={k} />;
}

interface Line {
  medicineId: string;
  batchNo: string;
  expiry: string;
  qty: string;
  cost: string;
  mal: string;
  hologram: boolean;
}
const emptyLine = (): Line => ({
  medicineId: '',
  batchNo: '',
  expiry: '',
  qty: '',
  cost: '',
  mal: '',
  hologram: false,
});

/** "Receive stock" (prototype receive drawer): supplier, invoice, second checker, lines. */
function ReceiveDrawer({
  medicines,
  suppliers,
  order,
  onClose,
}: {
  medicines: StockRow[];
  suppliers: Supplier[];
  /** RL-1650: receiving against an approved purchase order. */
  order?: OrderRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const dir = useDirectory(true);
  const [supplierId, setSupplierId] = useState(order?.supplierId ?? '');
  const [invoice, setInvoice] = useState('');
  const [checker, setChecker] = useState('');
  const [lines, setLines] = useState<Line[]>(
    order
      ? order.lines.map((l) => ({
          ...emptyLine(),
          medicineId: l.medicine.id,
          qty: String(l.qty - (l.receivedQty ?? 0)),
        }))
      : [emptyLine()],
  );
  // RL-1650: a short-expiry batch (409 SHORT_EXPIRY) is accepted with a reason.
  const [shortExpiry, setShortExpiry] = useState<string | null>(null);
  const [shortReason, setShortReason] = useState('');
  const receive = useMutation({
    mutationFn: (body: unknown) => api.post('/stock/receive', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
  // Any change to the items asks again whether a short expiry is accepted.
  const changeLines = (next: (ls: Line[]) => Line[]) => {
    setShortExpiry(null);
    setLines(next);
  };
  const set = (i: number, patch: Partial<Line>) =>
    changeLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const submit = () => {
    const parsed = lines.map((l) => ({
      medicineId: l.medicineId,
      batchNo: l.batchNo.trim(),
      expiry: l.expiry,
      qty: Number(l.qty),
      unitCostSen: l.cost.trim() ? parseRM(l.cost) : undefined,
      malNumber: l.mal.trim() || undefined,
      hologramChecked: l.hologram,
    }));
    if (
      !supplierId ||
      invoice.trim().length < 2 ||
      !checker ||
      parsed.some(
        (l) =>
          !l.medicineId ||
          !l.batchNo ||
          !l.expiry ||
          !Number.isInteger(l.qty) ||
          l.qty < 1 ||
          l.unitCostSen === null,
      )
    )
      return toast.show({
        tone: 'error',
        title: 'Complete the delivery details',
      });
    if (shortExpiry && shortReason.trim().length < 3)
      return toast.show({
        tone: 'error',
        title: 'Give the reason for accepting the short expiry',
      });
    receive
      .mutateAsync({
        ...(order ? { orderId: order.id } : {}),
        supplierId,
        supplierInvoiceNo: invoice.trim(),
        checkedById: checker,
        lines: parsed,
        ...(shortExpiry
          ? { acceptShortExpiry: true, shortExpiryReason: shortReason.trim() }
          : {}),
      })
      .then(() => {
        toast.show({ tone: 'success', title: 'Stock received' });
        onClose();
      })
      .catch((err: unknown) => {
        if (isApiError(err) && err.code === 'SHORT_EXPIRY') {
          setShortExpiry(err.message);
          return;
        }
        toast.show({
          tone: 'error',
          title: 'Not received',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        });
      });
  };
  const medOptions = medicines.map((m) => ({ value: m.id, label: m.name }));
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={order ? `Receive · ${order.number}` : 'Receive stock'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={receive.isPending}
            onClick={submit}
          >
            Receive
          </Button>
        </>
      }
    >
      <div className={local.drawerForm}>
        <Field label="Supplier">
          <Select
            value={supplierId}
            placeholder="Choose…"
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(e) => setSupplierId(e.target.value)}
          />
        </Field>
        <Field label="Supplier invoice number">
          <Input
            value={invoice}
            maxLength={60}
            onChange={(e) => setInvoice(e.target.value)}
          />
        </Field>
        <Field
          label="Checked by (second person)"
          hint="Someone other than you checks the delivery."
        >
          <Select
            value={checker}
            placeholder="Choose…"
            options={(dir.data?.staff ?? []).map((s) => ({
              value: s.id,
              label: s.fullName,
            }))}
            onChange={(e) => setChecker(e.target.value)}
          />
        </Field>
        {lines.map((l, i) => (
          <ReceiveLine
            key={i}
            n={i + 1}
            line={l}
            options={medOptions}
            onChange={(p) => set(i, p)}
            onRemove={
              lines.length > 1
                ? () => changeLines((ls) => ls.filter((_, j) => j !== i))
                : undefined
            }
          />
        ))}
        <Button
          size="sm"
          icon={<Plus size={16} />}
          onClick={() => changeLines((ls) => [...ls, emptyLine()])}
        >
          Add another item
        </Button>
        {shortExpiry && (
          <div className={local.cardNote} role="alert">
            <p>{`${shortExpiry}. Receive it anyway with a reason, or remove the item.`}</p>
            <Field label="Reason for accepting the short expiry">
              <Input
                value={shortReason}
                maxLength={200}
                onChange={(e) => setShortReason(e.target.value)}
              />
            </Field>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function ReceiveLine({
  n,
  line: l,
  options,
  onChange,
  onRemove,
}: {
  n: number;
  line: Line;
  options: { value: string; label: string }[];
  onChange: (p: Partial<Line>) => void;
  onRemove?: () => void;
}): ReactNode {
  return (
    <fieldset className={local.lineSet}>
      <legend className="eyebrow">{`Item ${n}`}</legend>
      <Field label="Medicine">
        <Select
          value={l.medicineId}
          placeholder="Choose…"
          options={options}
          onChange={(e) => onChange({ medicineId: e.target.value })}
        />
      </Field>
      <div className={local.twoCol}>
        <Field label="Batch number">
          <Input
            value={l.batchNo}
            maxLength={40}
            onChange={(e) => onChange({ batchNo: e.target.value })}
          />
        </Field>
        <Field label="Expiry">
          <Input
            type="date"
            value={l.expiry}
            onChange={(e) => onChange({ expiry: e.target.value })}
          />
        </Field>
        <Field label="Quantity">
          <Input
            inputMode="numeric"
            value={l.qty}
            onChange={(e) => onChange({ qty: e.target.value })}
          />
        </Field>
        <Field label="Unit cost (RM, optional)">
          <Input
            inputMode="decimal"
            value={l.cost}
            onChange={(e) => onChange({ cost: e.target.value })}
          />
        </Field>
      </div>
      <Field label="MAL registration (optional)">
        <Input
          value={l.mal}
          maxLength={40}
          onChange={(e) => onChange({ mal: e.target.value })}
        />
      </Field>
      <ChipToggle
        label="Security hologram checked"
        single
        options={[
          { value: 'yes', label: 'Checked' },
          { value: 'no', label: 'Not checked' },
        ]}
        selected={[l.hologram ? 'yes' : 'no']}
        onChange={(v) => onChange({ hologram: v[0] === 'yes' })}
      />
      {onRemove && (
        <Button
          size="sm"
          variant="ghost"
          icon={<Trash2 size={16} />}
          onClick={onRemove}
        >
          Remove item
        </Button>
      )}
    </fieldset>
  );
}
